import Decimal from "decimal.js";

export const parseFloat = (value) => {
  try {
    if (typeof value === 'number') return value;
    const number = Number.parseFloat(value.replaceAll(",", ""));
    if (value === "") return 0;
    if (isNaN(number)) return value;
    return number;
  } catch (e) {
    return 0;
  }
};


/**
 * Comprueba si un valor es ni undefined ni null.
 *
 * @param {any} value - El valor a verificar.
 * @return {boolean} Devuelve true si el valor no es undefined ni null, de lo contrario false.
 */
export function isValid(value) {
  return value !== undefined && value !== null;
}


/**
 * Remueve el decimal si el valor es 0
 *  
 * @param {any} value - El valor a verificar.
 * @param {number} decimalPlaces - La precisión del decimal.
 * @return {number} Devuelve el valor con el o los decimales removidos.
*/

export const removeDecimals = ({ number, decimalPlaces = 0 }) => {
  const roundedNumber = parseFloat(number)
  return parseFloat(roundedNumber?.toFixed(decimalPlaces).replace(/(\.0+|0+)$/, ''));
}

export const parseDecimal = (value) => {
  try {
    if (typeof value === 'number') return new Decimal(value).toString();
    const number = new Decimal(value.replaceAll(",", "")).toString();
    if (value === "") return 0;
    if (isNaN(number)) return value;
    return number;
  } catch (e) {
    return 0;
  }
};


// Clon propio para no alterar la configuración global de Decimal que usan los formularios.
const DisplayDecimal = Decimal.clone({
  precision: 60,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -60,
  toExpPos: 60
});

const DEFAULT_DISPLAY_PRECISION = 4;

export const resolveDisplayPrecision = (precision) => {
  const parsed = Number.parseInt(precision, 10);
  return Number.isNaN(parsed) || parsed < 0 ? DEFAULT_DISPLAY_PRECISION : parsed;
};

const toDisplayDecimal = (value) => {
  if (value === null || value === undefined || value === "") return null;
  try {
    const number = new DisplayDecimal(typeof value === "string" ? value.replaceAll(",", "").trim() : value);
    return number.isFinite() ? number : null;
  } catch (e) {
    return null;
  }
};

/**
 * Texto de presentación de un valor decimal a la precisión del usuario calificado:
 * redondeo ROUND_HALF_UP, ceros fijos y coma de miles. "1234.5" (4) → "1,234.5000".
 * Los valores vacíos o no numéricos se devuelven sin cambios.
 *
 * @param {number|string} value - Valor crudo, con comas de miles o número.
 * @param {number|string} precision - Decimales a mostrar; 4 si no es válida.
 * @return {string|*} El valor formateado, o el original si no es numérico.
 */
export const formatDecimal = (value, precision) => {
  const number = toDisplayDecimal(value);
  if (number === null) return value;

  const fixed = number.toFixed(resolveDisplayPrecision(precision));
  const negative = fixed.startsWith("-") && /[1-9]/.test(fixed);
  const [integer, decimals] = fixed.replace("-", "").split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return `${negative ? "-" : ""}${grouped}${decimals !== undefined ? `.${decimals}` : ""}`;
};

/**
 * Valor decimal sin ceros sobrantes ni coma de miles, redondeado ROUND_HALF_UP a la precisión.
 * Sin precisión (null/undefined) no redondea. "8500.000" (4) → "8500". Es el value que
 * recibe un NumericFormat con thousandSeparator, que ya agrega la coma.
 *
 * @param {number|string} value - Valor crudo, con comas de miles o número.
 * @param {number|string|null} [precision] - Decimales máximos; sin valor no redondea.
 * @return {string|*} El valor sin ceros sobrantes, o el original si no es numérico.
 */
export const roundDecimalCompact = (value, precision) => {
  const number = toDisplayDecimal(value);
  if (number === null) return value;

  const rounded = precision === null || precision === undefined
    ? number
    : number.toDecimalPlaces(resolveDisplayPrecision(precision));

  return rounded.isZero() ? "0" : rounded.toFixed();
};

/**
 * Igual que roundDecimalCompact pero con coma de miles, para inputs de texto.
 * "8500.000" (4) → "8,500"; "0.0029021" sin precisión → "0.0029021".
 *
 * @param {number|string} value - Valor crudo, con comas de miles o número.
 * @param {number|string|null} [precision] - Decimales máximos; sin valor no redondea.
 * @return {string|*} El valor formateado, o el original si no es numérico.
 */
export const formatDecimalCompact = (value, precision) => {
  if (toDisplayDecimal(value) === null) return value;

  const compact = roundDecimalCompact(value, precision);
  const negative = compact.startsWith("-");
  const [integer, decimals] = compact.replace("-", "").split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return `${negative ? "-" : ""}${grouped}${decimals !== undefined ? `.${decimals}` : ""}`;
};

/**
 * Suma exacta, sin redondear. Ignora los valores vacíos o no numéricos.
 *
 * @param {Array<number|string>} values - Valores a sumar.
 * @return {string} La suma como string.
 */
export const sumDecimal = (values = []) =>
  values
    .reduce((total, value) => {
      const number = toDisplayDecimal(value);
      return number === null ? total : total.plus(number);
    }, new DisplayDecimal(0))
    .toFixed();

/**
 * Texto de presentación del factor de conversión: sin decimales si es entero; si no, rellena con
 * ceros hasta la precisión del usuario calificado y no recorta lo que tenga más decimales.
 * "1.000000000000000000" (4) → "1"; "1.5" (4) → "1.5000"; "2.12345" (4) → "2.12345".
 * Los valores vacíos o no numéricos se devuelven sin cambios.
 *
 * @param {number|string} value - Factor crudo.
 * @param {number|string} precision - Decimales mínimos si el factor no es entero; 4 si no es válida.
 * @return {string|*} El factor formateado, o el original si no es numérico.
 */
export const formatConversionFactor = (value, precision) => {
  const number = toDisplayDecimal(value);
  if (number === null) return value;

  const decimals = number.decimalPlaces();
  if (decimals === 0) return number.isZero() ? "0" : number.toFixed();

  return number.toFixed(Math.max(resolveDisplayPrecision(precision), decimals));
};
